import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Custom_Field_Insert_Input,
  Coassign_Custom_Field_Set_Input,
  Coassign_Custom_Field_Choice_Updates
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { withStage } from '@/lib/layered'
import type { DB } from '@/lib/kysely-d'
import { sql, type Kysely } from 'kysely'
import { type Reactive } from 'vue'

const TABLE_NAME = 'custom_field' as const

export enum ValueTypeKey {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATETIME = 'DATETIME',
  CHOSE_SINGLE = 'CHOSE_SINGLE',
  CHOSE_MULTIPLE = 'CHOSE_MULTIPLE'
}

export const ValueType: Record<ValueTypeKey, string> = {
  [ValueTypeKey.STRING]: 'customField.STRING',
  [ValueTypeKey.NUMBER]: 'customField.NUMBER',
  [ValueTypeKey.DATETIME]: 'customField.DATETIME',
  [ValueTypeKey.CHOSE_SINGLE]: 'customField.CHOSE_SINGLE',
  [ValueTypeKey.CHOSE_MULTIPLE]: 'customField.CHOSE_MULTIPLE'
}

const isUniqueKey = async ({ id, key }: { id: string; key: string }): Promise<Boolean> => {
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('key')
      .where('key', '=', key)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

const fetchMaxSortOrder = async (): Promise<number> => {
  const withStageMaxOrder = await withStage(async (db: Kysely<DB>) => {
    const result = await db
      .selectFrom(TABLE_NAME)
      .select(sql<number>`MAX("order")`.as('order'))
      .executeTakeFirst()
    return result || { order: 0 }
  })
  return withStageMaxOrder.order
}

const getDefaultOrder = async (): Promise<number> => {
  const maxSortOrder = await fetchMaxSortOrder()
  return maxSortOrder + 1
}

// 先頭はA-zのみ、それに続くのはA-z, 0-9, "-", "_"の文字
const regex = /^[A-Za-z][A-Za-z0-9\-_]*$/

export const customFieldSchema = z
  .object({
    id: z.string().max(21),
    name: z.string().trim().min(2).max(16),
    key: z.string().min(2).max(32).regex(regex),
    description: z.string().max(128).nullable().optional(),
    value_type: z.enum(Object.keys(ValueType) as [string, ...string[]]),
    visible: z.boolean().default(true),
    required: z.boolean().default(true),
    order: z.number().default(0)
  })
  .refine(isUniqueKey, { message: 'キー名が重複しています', path: ['key'] })

export type CustomField = z.infer<typeof customFieldSchema>

export class CustomFieldMutator extends Mutator<typeof customFieldSchema> {
  static readonly stageableClassName = 'CustomFieldMutator'
  schema = customFieldSchema
  table = TABLE_NAME

  static async load(id: string): Promise<Reactive<CustomFieldMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    if (!base) {
      const order = await getDefaultOrder()
      self.context = { id, order }
    } else {
      self.context = { id }
    }
    // 更新の場合、型を、DB用から一般化
    if (base) {
      self.base = {
        ...base,
        ...{ visible: Boolean(base.visible), required: Boolean(base.required) }
      }
    }
    return self
  }

  canTakeChoises(): boolean {
    return (
      this.merged.value_type === ValueTypeKey.CHOSE_SINGLE ||
      this.merged.value_type === ValueTypeKey.CHOSE_MULTIPLE
    )
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )

    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...params,
      ...{ visible: Number(params.visible), required: Number(params.required) }
    }
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Custom_Field_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertCustomFieldMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const deletedAt = formatISO(new Date())
    const variables: Coassign_Custom_Field_Set_Input = {
      commit_id: commitId,
      deleted_at: deletedAt
    }
    const choices_variables: Coassign_Custom_Field_Choice_Updates = {
      _set: { deleted_at: deletedAt, commit_id: commitId },
      where: { custom_field_id: { _eq: id } }
    }
    // custom_field_choice を削除する際、 親 custom_field が未削除かどうかをチェックするため、
    // 先に custom_field_choice を削除する
    return [
      {
        mutation: updateCustomFieldChoiceManyMutationDocument,
        variables: { updates: [choices_variables] }
      },
      { mutation: updateCustomFieldMutationDocument, variables: { id: id, input: variables } }
    ]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Custom_Field_Set_Input = { ...params, ...{ commit_id } }
    return [
      { mutation: updateCustomFieldMutationDocument, variables: { id: id, input: variables } }
    ]
  }
}

const insertCustomFieldMutationDocument = graphql(/* GraphQL */ `
  mutation insertCustomField($input: coassign_custom_field_insert_input!) {
    insert_coassign_custom_field_one(object: $input) {
      created_at
    }
  }
`)

const updateCustomFieldMutationDocument = graphql(/* GraphQL */ `
  mutation updateCustomField($id: String!, $input: coassign_custom_field_set_input!) {
    update_coassign_custom_field_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const updateCustomFieldChoiceManyMutationDocument = graphql(/* GraphQL */ `
  mutation updateCustomFieldChoiceMany($updates: [coassign_custom_field_choice_updates!]!) {
    update_coassign_custom_field_choice_many(updates: $updates) {
      affected_rows
    }
  }
`)
